import { Button } from "@sellar-io/components";
import { daysOfWeek } from "@sellar-io/common";
import dayjs from "dayjs";
import { useId, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Alert } from "../../../components/alert/Alert";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useCourierDeliveryScheduleCreate } from "../../../hooks/api/v2/courierDeliverySchedule/useCourierDeliveryScheduleCreate";
import { CourierScheduleForm } from "../CourierDeliverySchedule/forms/CourierScheduleForm";

export type CourierDeliveryScheduleFormType = {
  orderCutOffTime: string;
  dispatchDaysAfterConfirmation: number;
  deliveryDaysAfterDispatch: number;
  startAt: string | null;
  allowSaturdayDelivery: boolean;
};
export const CourierScheduleCreateDialog = ({
  active,
  onDismiss,
}: {
  active: boolean;
  onDismiss: () => void;
}) => {
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const scrollToRef = useRef<HTMLDivElement | null>(null);
  const formId = useId();
  const form = useForm<CourierDeliveryScheduleFormType>({
    shouldUseNativeValidation: true,
    defaultValues: {
      orderCutOffTime: "15:00",
      dispatchDaysAfterConfirmation: 1,
      deliveryDaysAfterDispatch: 1,
      startAt: null,
      allowSaturdayDelivery: false,
    },
  });

  const [selectedDays, setSelectedDays] = useState<daysOfWeek[]>([]);

  const courierDeliveryScheduleCreateMutation =
    useCourierDeliveryScheduleCreate();
  const onSubmit = (data: CourierDeliveryScheduleFormType) => {
    setShowWarning(false);
    if (!selectedDays.length) {
      setShowWarning(true);
      return scrollToRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    courierDeliveryScheduleCreateMutation.mutate(
      {
        orderCutOffTime: dayjs()
          .utc()
          .set("hour", parseInt(data.orderCutOffTime.split(":")[0] ?? ""))
          .set("minute", parseInt(data.orderCutOffTime.split(":")[1] ?? ""))
          .toDate(),
        dispatchDaysAfterConfirmation: parseInt(
          data.dispatchDaysAfterConfirmation.toString(),
        ),
        deliveryDaysAfterDispatch: parseInt(
          data.deliveryDaysAfterDispatch.toString(),
        ),
        days: selectedDays,
        startAt: data.startAt ? new Date(data.startAt) : null,
        allowSaturdayDelivery: data.allowSaturdayDelivery,
      },
      {
        onSuccess: () => onDismiss(),
      },
    );
  };

  const description =
    "Give your customers an accurate delivery date prediction for orders fulfilled by courier. Your given dispatch and delivery dates will be automatically pre-filled when finalising courier orders.";

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <div ref={scrollToRef}></div>
      <DialogNavigation
        title={"Courier delivery schedule"}
        description={description}
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />
      <DialogContent>
        {courierDeliveryScheduleCreateMutation.isError ? (
          <Alert
            type={"error"}
            message={
              apiV2AssertError(courierDeliveryScheduleCreateMutation.error)
                ?.response?.data.message ?? "Unknown error occurred."
            }
          />
        ) : null}
        <CourierScheduleForm
          selectedDays={selectedDays}
          setSelectedDays={setSelectedDays}
          form={form}
          onSubmit={onSubmit}
          showWarning={showWarning}
          formId={formId}
        />
      </DialogContent>
      <DialogCTA>
        <Button
          type="submit"
          form={formId}
          loading={courierDeliveryScheduleCreateMutation.isPending}
        >
          Confirm
        </Button>
      </DialogCTA>
    </Dialog>
  );
};
